import { type CSSProperties } from 'react';
import clsx from 'clsx';
import { type BreakdownItem } from './BreakdownLegend';

type Props = {
  items: BreakdownItem[];
  // Denominator for each cell's % readout. Keep separate from the items sum
  // so callers can express "share of total" even when the visible cells are
  // a subset.
  total: number;
  formatValue: (value: number) => string;
  // Optional controlled hover state synced with the paired chart (bar or
  // donut). Pass both props or neither — passing only one disables hover
  // tracking. Non-hovered cells dim to 40% so the matching slice/segment
  // remains the visual focus.
  hovered?: string | null;
  onHoverChange?: (key: string | null) => void;
  // Override the column count on `sm:` and above. Defaults to items.length.
  // Mobile defaults to a single column so cells get full width and the dot/
  // label and $/% rows stay legible; set `mobileColumns` to override (e.g.
  // when the card already has enough horizontal room to read two columns).
  columns?: number;
  mobileColumns?: number;
};

// Tailwind's purger needs the column class to be a literal string in source,
// so we materialize the four breakpoints we actually use rather than
// templating `sm:grid-cols-${n}`.
const COL_CLASS: Record<number, string> = {
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-4',
};

const MOBILE_COL_CLASS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
};

export function BreakdownLegendGrid({
  items,
  total,
  formatValue,
  hovered,
  onHoverChange,
  columns,
  mobileColumns,
}: Props) {
  const hoverEnabled = onHoverChange !== undefined;
  const colCount = columns ?? items.length;
  const colClass = COL_CLASS[colCount] ?? COL_CLASS[3];
  const mobileColClass = MOBILE_COL_CLASS[mobileColumns ?? 1] ?? 'grid-cols-1';

  return (
    <div className={clsx('grid gap-4', mobileColClass, colClass)}>
      {items.map((item) => {
        const pct = total > 0 ? (item.value / total) * 100 : 0;
        const isFocused = hoverEnabled && hovered === item.key;
        const dimmed = hoverEnabled && hovered != null && !isFocused;
        return (
          <div
            key={item.key}
            onMouseEnter={() => onHoverChange?.(item.key)}
            onMouseLeave={() => onHoverChange?.(null)}
            className="flex flex-col gap-0.5 px-2 py-1.5 -mx-2 rounded-md cursor-default"
            style={{
              backgroundColor: isFocused ? 'var(--berg-overlay)' : 'transparent',
              opacity: dimmed ? 0.4 : 1,
              transition: 'opacity 0.18s ease, background-color 0.15s ease',
            }}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="w-2 h-2 rounded-full flex-none dot-glow"
                style={
                  {
                    background: item.color,
                    '--dot-color': item.color,
                  } as CSSProperties
                }
              />
              <span className="text-sm text-fg-secondary truncate">
                {item.label}
              </span>
            </div>
            <div className="flex items-baseline gap-2 pl-4 tabular">
              <span
                className={clsx(
                  'text-base font-semibold',
                  item.valueTone === 'neg' && 'text-red',
                  item.valueTone === 'pos' && 'text-green',
                )}
              >
                {formatValue(item.value)}
              </span>
              <span className="text-xs text-fg-tertiary">
                {pct.toFixed(0)}%
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
